import { Entity } from '@backstage/catalog-model';
import { useApi, configApiRef } from '@backstage/core-plugin-api';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { entityMatchesTypeFilter } from '../catalogDiscovery';
import { useSearchParams } from 'react-router-dom';

type OptionsFilters = {
  kinds: string[];
  types: string[];
  owners: string[];
  lifecycles: string[];
  businessUnits: string[];
};

type ApiDefinitionFilter = 'all' | 'openapi' | 'asyncapi';

export const useCatalog = () => {
  const [searchParams] = useSearchParams();

  const [allEntities, setAllEntities] = useState<Entity[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const [kindFilter, setKindFilter] = useState<string>(
    searchParams.get('kind') || 'component',
  );
  const [typeFilter, setTypeFilter] = useState<string>(
    searchParams.get('type') || 'service',
  );
  const [apiDefinitionFilter, setApiDefinitionFilter] =
    useState<ApiDefinitionFilter>('all');
  const [ownerFilter, setOwnerFilter] = useState<string>('all');
  const [lifecycleFilter, setLifecycleFilter] = useState<string>('all');
  const [businessUnitFilter, setBusinessUnitFilter] = useState<string>('all');

  const [optionsFilters, setOptionsFilters] = useState<OptionsFilters>({
    kinds: [],
    types: [],
    owners: [],
    lifecycles: [],
    businessUnits: [],
  });

  const config = useApi(configApiRef);

  useEffect(() => {
    fetchEntities();
  }, []);

  const transformAndSortedSetToArray = useCallback(
    (data: Set<string>): string[] => {
      return Array.from(data).sort((a, b) => a.localeCompare(b));
    },
    [],
  );

  const getOwnerName = useCallback((owner: any): string => {
    if (typeof owner === 'string') {
      return owner;
    }

    if (typeof owner === 'object') {
      const name = owner.name || owner.displayName || owner.title || owner.id;
      if (name && typeof name === 'string') {
        const squadMatch = new RegExp(/\/(.+)$/).exec(name);
        return squadMatch ? squadMatch[1] : name;
      }
      if (owner.metadata?.name && typeof owner.metadata.name === 'string') {
        const squadMatch = new RegExp(/\/(.+)$/).exec(owner.metadata.name);
        return squadMatch ? squadMatch[1] : owner.metadata.name;
      }
    }

    return '';
  }, []);

  const getLifecycleName = useCallback((lifecycle: any): string => {
    if (typeof lifecycle === 'string') {
      return lifecycle;
    }

    if (typeof lifecycle === 'object') {
      const name =
        lifecycle.name ||
        lifecycle.displayName ||
        lifecycle.title ||
        lifecycle.id ||
        lifecycle.lifecycle;
      if (name && typeof name === 'string') {
        return name;
      }
    }

    return '';
  }, []);

  const fetchEntities = useCallback(async () => {
    try {
      const backendUrl = config.getString('backend.baseUrl');
      const response = await fetch(`${backendUrl}/api/catalog/entities`);
      const data = await response.json();
      const fetchedEntities = data.items || [];

      const kinds = new Set<string>();
      const types = new Set<string>();
      const owners = new Set<string>();
      const lifecycles = new Set<string>();
      const businessUnits = new Set<string>();

      fetchedEntities.forEach((entity: Entity) => {
        // Kind
        const king = entity?.kind;
        if (typeof king === 'string') {
          kinds.add(king?.toLowerCase());
        }

        const type = (entity as any).spec?.type;
        if (type) {
          const typeStr = typeof type === 'string' ? type : String(type);
          if (!['openapi', 'asyncapi'].includes(typeStr?.toLowerCase())) {
            types.add(typeStr);
          }
        }

        const owner = (entity as any).spec?.owner;
        if (owner) {
          const ownerStr = getOwnerName(owner);
          if (ownerStr) owners.add(ownerStr);
        }

        const lifecycle = (entity as any).spec?.lifecycle;
        if (lifecycle) {
          const lifecycleStr = getLifecycleName(lifecycle);
          if (lifecycleStr) lifecycles.add(lifecycleStr);
        }

        // Extract business-unit from labels
        const businessUnit = entity.metadata.labels?.['business-unit'];
        if (typeof businessUnit === 'string') {
          businessUnits.add(businessUnit);
        }
      });

      setAllEntities(fetchedEntities);
      setOptionsFilters({
        kinds: ['all', ...transformAndSortedSetToArray(kinds)],
        types: ['all', ...transformAndSortedSetToArray(types)],
        owners: ['all', ...transformAndSortedSetToArray(owners)],
        lifecycles: ['all', ...transformAndSortedSetToArray(lifecycles)],
        businessUnits: ['all', ...transformAndSortedSetToArray(businessUnits)],
      });
    } catch (error) {
      console.error('Error fetching entities:', error);
    } finally {
      setLoading(false);
    }
  }, [config]);

  const entities = useMemo((): Entity[] => {
    let filtered = Array.from<Entity>(allEntities);

    if (kindFilter !== 'all') {
      filtered = filtered.filter(
        (entity: Entity) =>
          typeof entity?.kind === 'string' &&
          entity?.kind?.toLowerCase() === kindFilter,
      );
    }

    if (typeFilter !== 'all') {
      filtered = filtered.filter((entity: Entity) =>
        entityMatchesTypeFilter(entity, typeFilter, allEntities),
      );
    }

    if (apiDefinitionFilter !== 'all') {
      filtered = filtered.filter((entity: Entity) =>
        entityMatchesTypeFilter(entity, apiDefinitionFilter, allEntities),
      );
    }

    if (ownerFilter !== 'all') {
      filtered = filtered.filter((entity: Entity) => {
        const owner = (entity as any)?.spec?.owner;
        return (
          typeof owner === 'string' && owner?.toLowerCase() === ownerFilter
        );
      });
    }

    if (lifecycleFilter !== 'all') {
      filtered = filtered.filter((entity: Entity) => {
        const lifecycle = (entity as any)?.spec?.lifecycle;
        return (
          typeof lifecycle === 'string' &&
          lifecycle?.toLowerCase() === lifecycleFilter
        );
      });
    }

    if (businessUnitFilter !== 'all') {
      filtered = filtered.filter((entity: Entity) => {
        const businessUnit = entity.metadata?.labels?.['business-unit'];
        return (
          typeof businessUnit === 'string' &&
          businessUnit?.toLowerCase() === businessUnitFilter
        );
      });
    }

    const sorted = filtered.toSorted((a, b) => {
      const aDate = a.metadata?.labels?.['creation-date'];
      const bDate = b.metadata?.labels?.['creation-date'];

      const hasADate = Boolean(aDate);
      const hasBDate = Boolean(bDate);

      if (hasADate && !hasBDate) return -1;
      if (!hasADate && hasBDate) return 1;
      if (!hasADate && !hasBDate) return 0;
      if (!aDate || !bDate) return 0;

      return new Date(bDate).getTime() - new Date(aDate).getTime();
    });

    return sorted;
  }, [
    allEntities,
    kindFilter,
    typeFilter,
    apiDefinitionFilter,
    ownerFilter,
    lifecycleFilter,
    businessUnitFilter,
  ]);

  return {
    allEntities,
    entities,
    loading,
    kindFilter,
    typeFilter,
    apiDefinitionFilter,
    ownerFilter,
    lifecycleFilter,
    businessUnitFilter,
    optionsFilters,
    setKindFilter,
    setTypeFilter,
    setApiDefinitionFilter,
    setOwnerFilter,
    setLifecycleFilter,
    setBusinessUnitFilter,
  };
};
